import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bread, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      const msg = errMsg(err);
      if (msg === "Email not verified") {
        toast.info("Please verify your email first");
        navigate("/verify", { state: { email } });
        return;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-white" data-testid="login-page">
      <div className="hidden w-1/2 flex-col justify-between bg-[#0F172A] p-12 lg:flex">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#EA580C]">
            <Bread size={20} weight="duotone" className="text-white" />
          </div>
          <span className="font-heading text-lg font-bold text-white">BakeFlow</span>
        </div>
        <div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tighter text-white leading-tight">
            Every bake, tracked.<br />Every step, signed off.
          </h1>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-400">
            A 23-step pipeline for taking bakery products from idea to final delivery — sampling, costing, approvals, HS codes, batch photos and more.
          </p>
        </div>
        <p className="text-xs text-slate-500">BakeFlow Product Pipeline Management</p>
      </div>

      <div className="flex w-full items-center justify-center px-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#EA580C]">
              <Bread size={20} weight="duotone" className="text-white" />
            </div>
            <span className="font-heading text-lg font-bold text-slate-900">BakeFlow</span>
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Sign in</h2>
          <p className="mt-1 text-sm text-slate-500">Access your product pipeline dashboard</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">{error}</div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-semibold text-[#EA580C] hover:text-[#C2410C]">Forgot password?</Link>
              </div>
              <div className="relative">
                <Input id="password" data-testid="login-password-input" type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full bg-[#EA580C] font-semibold text-white hover:bg-[#C2410C]">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-slate-500">
            No account?{" "}
            <Link to="/register" data-testid="goto-register-link" className="font-semibold text-[#EA580C] hover:text-[#C2410C]">Create one</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
